<script setup>
definePage({
  meta: {
    layout: 'blank',
    public: true,
  },
})
</script>

<template>
  <VContainer class="py-8">
    <VRow justify="center">
      <VCol
        cols="12"
        md="10"
        lg="8"
      >
        <VCard
          elevation="2"
          class="pa-6"
        >
          <h4 class="text-h4 font-weight-bold mb-4 text-center">
            TERMOS E CONDIÇÕES DE USO
          </h4>

          <h4 class="text-h5 font-weight-bold mb-4 text-center">
            PLATAFORMA EVENTOS CONNECT
          </h4>

          <VCardText class="text-body-1">
            <!-- Introdução -->
            <p class="mb-4">
              Este Termo de Uso aplica-se a todos os usuários da plataforma digital Eventos Connect, sejam pessoas físicas ou jurídicas,
              que a utilizem com finalidade pessoal ou profissional, incluindo, mas não se limitando a: organizadores de eventos,
              fornecedores de produtos e serviços, prestadores de serviços especializados (doravante denominados, individual ou coletivamente,
              "Usuário" ou "Você").
            </p>

            <p class="mb-4">
              Este instrumento estabelece as regras, deveres e obrigações que devem ser observadas ao acessar e utilizar a plataforma
              Eventos Connect ("Plataforma"), de titularidade da MKT Connect, pessoa jurídica de direito privado, inscrita no CNPJ sob o nº
              01.945.991/0001-48, com endereço fiscal à Alameda do Ingá, 585, Apt 1901, Torre 8, Vale Sereno, na Cidade de Nova Lima,
              Estado de Minas Gerais, doravante denominada "Empresa" ou "Plataforma".
            </p>

            <p class="mb-4">
              A Plataforma Eventos Connect tem por objetivo proporcionar um ambiente digital estratégico em que Organizadores de Evento — sejam
              profissionais ou usuários ocasionais — possam localizar e se conectar diretamente a fornecedores de serviços, produtos ou espaços
              voltados à realização de eventos. Trata-se de um espaço virtual de divulgação, consulta e interação entre usuários, sem interferência
              ou intermediação da Plataforma nas tratativas ou contratações que eventualmente venham a ocorrer.
            </p>

            <p class="mb-4">
              A utilização da plataforma depende da aceitação integral e inequívoca deste termo de uso, que constitui um contrato com plena validade jurídica.
            </p>

            <VAlert
              type="info"
              variant="tonal"
              class="mb-6"
            >
              <VIcon
                icon="mdi-information"
                class="me-2"
              />
              <strong>Aceitação dos Termos:</strong> A aceitação será formalizada de forma eletrônica, mediante clique no botão "Li e Aceito os Termos e Condições de Uso".
              Dúvidas poderão ser enviadas para: <a
                href="mailto:contato@eventosconnect.com.br"
                class="text-decoration-none"
              >contato@eventosconnect.com.br</a>.
            </VAlert>

            <p class="mb-6">
              Seus dados pessoais são importantes para nós. As informações sobre quais dados serão coletados, como serão tratados e para quais finalidades,
              estão detalhadas neste Termo e na <a
                href="/politica-privacidade"
                class="text-decoration-none"
              >Política de Privacidade</a>, que o complementa e deve ser lida com atenção.
            </p>

            <VDivider class="my-8" />

            <!-- Seção 1: Glossário -->
            <VCardSubtitle class="text-h5 font-weight-medium mb-6 px-0">
              1. GLOSSÁRIO DE TERMOS UTILIZADOS NO DOCUMENTO
            </VCardSubtitle>

            <p class="mb-4">
              Para facilitar o entendimento destes Termos de Uso, os termos abaixo terão o seguinte significado:
            </p>

            <VList
              class="mb-6"
              rounded
            >
              <VListItem class="px-4 py-3">
                <VListItemContent>
                  <strong>"Plataforma":</strong> Significa o ambiente digital desenvolvido e operado pela MKT Connect sob a marca Eventos Connect,
                  acessível por meio do endereço eletrônico ou por outros canais oficiais, incluindo sistemas, interfaces, aplicativos, processos e redes vinculadas.
                </VListItemContent>
              </VListItem>
              <VDivider />
              <VListItem class="px-4 py-3">
                <VListItemContent>
                  <strong>"Usuário":</strong> Pessoa física ou jurídica que acessa a Plataforma para fins de navegação, consulta, interação,
                  independentemente de cadastro ou contratação de serviços.
                </VListItemContent>
              </VListItem>
              <VDivider />
              <VListItem class="px-4 py-3">
                <VListItemContent>
                  <strong>"Organizador de Evento" ou "Usuário Organizador":</strong> Pessoa física ou jurídica que utiliza a Plataforma com a finalidade
                  de planejar e gerenciar eventos, podendo buscar, comparar e contatar fornecedores de produtos, serviços ou espaços.
                </VListItemContent>
              </VListItem>
              <VDivider />
              <VListItem class="px-4 py-3">
                <VListItemContent>
                  <strong>"Anunciante", "Fornecedor" ou "Prestador de Serviços e/ou Espaços":</strong> Pessoa física ou jurídica que se cadastre na Plataforma
                  com o objetivo de divulgar seus produtos, serviços ou espaços voltados à realização de eventos.
                </VListItemContent>
              </VListItem>
              <VDivider />
              <VListItem class="px-4 py-3">
                <VListItemContent>
                  <strong>"Vitrine" ou "Loja Virtual Personalizada":</strong> Página digital criada pelo Anunciante dentro da Plataforma, na qual são inseridas
                  informações relevantes sobre seus serviços ou espaços, como descrições, diferenciais, imagens, vídeos, formas de contato e outras funcionalidades disponíveis.
                </VListItemContent>
              </VListItem>
              <VDivider />
              <VListItem class="px-4 py-3">
                <VListItemContent>
                  <strong>"Curadoria":</strong> Processo interno conduzido pela Plataforma para análise e validação do cadastro de Anunciantes e fornecedores,
                  com o objetivo de manter a qualidade, segurança e adequação dos perfis ao ambiente digital da Eventos Connect.
                </VListItemContent>
              </VListItem>
              <VDivider />
              <VListItem class="px-4 py-3">
                <VListItemContent>
                  <strong>"Avaliação de Serviços":</strong> Funcionalidade da Plataforma que permite ao Organizador registrar sua opinião sobre os serviços
                  prestados por Anunciantes com os quais tenha interagido ou contratado.
                </VListItemContent>
              </VListItem>
              <VDivider />
              <VListItem class="px-4 py-3">
                <VListItemContent>
                  <strong>"Planner digital":</strong> Ambiente virtual exclusivo oferecido ao Organizador de Evento dentro da plataforma Eventos Connect,
                  com ferramentas para planejar e acompanhar todos os detalhes do evento.
                </VListItemContent>
              </VListItem>
            </VList>

            <VDivider class="my-8" />

            <!-- Seção 2: Informações Gerais -->
            <VCardSubtitle class="text-h5 font-weight-medium mb-6 px-0">
              2. INFORMAÇÕES GERAIS SOBRE A PLATAFORMA
            </VCardSubtitle>

            <VCardSubtitle class="text-h6 font-weight-medium mb-4">
              2.1 Objetivo da plataforma
            </VCardSubtitle>
            <VCardText class="py-0">
              <p class="mb-4">
                A Eventos Connect é uma plataforma digital operada pela MKT Connect, acessível por meio da internet (navegadores web).
              </p>
              <p class="mb-4">
                A Plataforma Eventos Connect consiste em um ambiente digital voltado à divulgação de serviços, produtos e espaços relacionados
                à realização de eventos. Por meio da Plataforma, fornecedores ("Anunciantes") podem apresentar seus serviços ao público interessado,
                enquanto Organizadores de Evento podem localizar e entrar em contato diretamente com esses profissionais.
              </p>
              <p class="mb-4">
                A Empresa não atua como intermediadora, agente ou representante de qualquer das partes envolvidas. As negociações, contratações
                e responsabilidades decorrentes da relação estabelecida entre Anunciante e Organizador ocorrem de forma autônoma e independente,
                sem qualquer participação, interferência, intermediação ou validação da Plataforma.
              </p>
            </VCardText>

            <VCardSubtitle class="text-h6 font-weight-medium mb-4">
              2.2 Funcionalidades para o Organizador
            </VCardSubtitle>
            <VCardText class="py-0">
              <p class="mb-4">
                Para o Organizador, a proposta da Plataforma é oferecer uma ferramenta semelhante a um planner digital, atualizado em tempo real,
                que permita realizar a gestão completa do evento, incluindo o controle de contratações, orçamentos, listas de convidados e demais
                informações relevantes, com possibilidade de incluir até dois assessores vinculados à organização do evento.
              </p>
              <p class="mb-4">
                As funcionalidades oferecidas podem ser ajustadas ou ampliadas pela Plataforma, a seu exclusivo critério, visando à melhoria
                contínua dos serviços e da experiência dos usuários.
              </p>
            </VCardText>

            <VCardSubtitle class="text-h6 font-weight-medium mb-4">
              2.3 Responsabilidade do Anunciante
            </VCardSubtitle>
            <VCardText class="py-0">
              <p class="mb-4">
                O Organizador está ciente de que cabe exclusivamente ao Anunciante a responsabilidade pelo conteúdo publicado em sua vitrine,
                pela veracidade das informações apresentadas, pela execução dos serviços eventualmente contratados e pelo cumprimento de todas
                as obrigações legais e contratuais assumidas junto aos Organizadores e/ou qualquer outra pessoa que o tenha contratado através da plataforma.
              </p>
            </VCardText>

            <VAlert
              type="warning"
              variant="tonal"
              class="mb-6"
            >
              <VIcon
                icon="mdi-currency-usd"
                class="me-2"
              />
              <strong>Política de Gratuidade:</strong> A utilização da plataforma pela empresa organizadora de eventos é, neste momento, gratuita.
              No entanto, a MKT Connect se reserva o direito de, a seu exclusivo critério, alterar essa condição e estabelecer a cobrança pelo
              acesso à plataforma, total ou parcialmente, a qualquer tempo, com comunicação prévia de 30 dias.
            </VAlert>

            <VDivider class="my-8" />

            <!-- Seção 3: Cadastro e Responsabilidades -->
            <VCardSubtitle class="text-h5 font-weight-medium mb-6">
              3. CADASTRO E RESPONSABILIDADES DO USUÁRIO
            </VCardSubtitle>

            <VCardSubtitle class="text-h6 font-weight-medium mb-4">
              3.1 Requisito de cadastro
            </VCardSubtitle>
            <VCardText class="py-0">
              <p class="mb-4">
                O cadastro na Plataforma é permitido a pessoas físicas e jurídicas que atuem, de forma profissional ou eventual, no setor de eventos.
                Para utilizar as funcionalidades da Plataforma, o Usuário deve fornecer informações completas, corretas e atualizadas, assumindo
                total responsabilidade por sua veracidade.
              </p>
            </VCardText>
            <VCardSubtitle class="text-h6 font-weight-medium mb-4">
              3.2 Atualização de informações
            </VCardSubtitle>

            <VCardText class="py-0">
              <p class="mb-4">
                O Usuário compromete-se a manter seus dados cadastrais sempre atualizados e a responder, em tempo hábil, às eventuais solicitações
                de atualização formuladas pela Empresa. O não atendimento poderá acarretar a suspensão temporária ou definitiva do acesso à Plataforma,
                sem qualquer direito a indenização ou reembolso.
              </p>
            </VCardText>

            <VCardSubtitle class="text-h6 font-weight-medium mb-4">
              3.3 Responsabilidade pelas Informações e Backup dos Dados
            </VCardSubtitle>
            <VCardText class="py-0">
              <p class="mb-4">
                O Organizador de Evento é o único responsável pelas informações, documentos e conteúdos que inseriu na Plataforma durante a gestão
                de seus eventos, incluindo dados de convidados, cronogramas, orçamentos, contratações e demais registros vinculados à organização do evento.
              </p>
              <p class="mb-4">
                A Eventos Connect não se responsabiliza pelo arquivamento, armazenamento ou backup dessas informações, nem garante sua preservação
                após o encerramento do evento ou eventual inatividade da conta. Cabe exclusivamente ao Organizador manter cópia externa e segura
                de todos os dados inseridos na Plataforma.
              </p>
            </VCardText>

            <VCardSubtitle class="text-h6 font-weight-medium mb-4">
              3.4 Condutas vedadas
            </VCardSubtitle>
            <VCardText class="py-0">
              <p class="mb-4">
                É expressamente proibida a publicação de conteúdo ofensivo, difamatório, discriminatório, com conotação ilegal, com violação de
                direitos autorais ou que infrinja a legislação vigente, especialmente a Lei Geral de Proteção de Dados Pessoais (LGPD).
              </p>
            </VCardText>

            <VCardSubtitle class="text-h6 font-weight-medium mb-4">
              3.5 Declaração de capacidade e legitimidade
            </VCardSubtitle>
            <VCardText class="py-0">
              <p class="mb-4">
                Ao aderir a estes Termos, o Usuário declara expressamente que não possui qualquer impedimento legal para se cadastrar ou utilizar
                a Plataforma, responsabilizando-se integralmente por todos os atos praticados em seu nome.
              </p>
            </VCardText>

            <VDivider class="my-8" />

            <!-- Seção 4: Curadoria -->
            <VCardSubtitle class="text-h5 font-weight-medium mb-6">
              4. CURADORIA E APROVAÇÃO DE CADASTRO
            </VCardSubtitle>

            <VCardText class="py-0">
              <p class="mb-4">
                Com o objetivo de assegurar a qualidade, segurança e integridade dos perfis divulgados na Plataforma, todos os cadastros de
                Organizadores de eventos estarão sujeitos à curadoria prévia. Esse processo consistirá em análise manual das informações fornecidas
                no momento do cadastro, incluindo, mas não se limitando a: nome ou razão social, documentos, descrição dos serviços, imagens e
                adequação ao setor de eventos.
              </p>
            </VCardText>

            <VList
              class="mb-6"
              rounded
            >
              <VListItem class="px-4 py-3">
                <VListItemContent>
                  A aprovação do cadastro é requisito indispensável para a ativação do perfil público e utilização dos recursos da Plataforma.
                </VListItemContent>
              </VListItem>
              <VDivider />
              <VListItem class="px-4 py-3">
                <VListItemContent>
                  O envio de informações incompletas, inverídicas, incompatíveis com os objetivos da Plataforma ou que desrespeitem os padrões
                  editoriais e éticos estabelecidos poderá resultar na reprovação do cadastro.
                </VListItemContent>
              </VListItem>
              <VDivider />
              <VListItem class="px-4 py-3">
                <VListItemContent>
                  A curadoria poderá ser repetida a qualquer tempo, inclusive após a ativação do perfil, caso a Plataforma identifique
                  inconsistências, condutas irregulares ou necessidade de revalidação das informações.
                </VListItemContent>
              </VListItem>
            </VList>

            <VDivider class="my-8" />

            <!-- Seção 5: Exclusão de Acesso -->
            <VCardSubtitle class="text-h5 font-weight-medium mb-6">
              5. EXCLUSÃO DE ACESSO E RESPONSABILIDADE DA PLATAFORMA
            </VCardSubtitle>

            <VCardSubtitle class="text-h6 font-weight-medium mb-4">
              5.1 Exclusão pelo Organizador
            </VCardSubtitle>
            <VCardText class="py-0">
              <p class="mb-4">
                O Organizador de Evento poderá, a qualquer tempo, excluir sua conta e as informações por ele inseridas diretamente por meio das
                funcionalidades disponíveis na própria Plataforma. A exclusão da conta implicará a remoção definitiva dos dados inseridos,
                ressalvadas as hipóteses previstas na Política de Privacidade.
              </p>
            </VCardText>

            <VCardSubtitle class="text-h6 font-weight-medium mb-4">
              5.2 Exclusão pela Plataforma
            </VCardSubtitle>
            <VCardText class="py-0">
              <p class="mb-4">
                A Eventos Connect também se reserva o direito de, a seu exclusivo critério, excluir ou suspender o cadastro, as informações e o
                acesso do Organizador de Evento à Plataforma, a qualquer tempo, inclusive sem justificativa específica, tendo em vista que o
                serviço é gratuito.
              </p>
            </VCardText>

            <VCardSubtitle class="text-h6 font-weight-medium mb-4">
              5.3 Encerramento da Plataforma
            </VCardSubtitle>
            <VCardText class="py-0">
              <p class="mb-4">
                A Eventos Connect poderá encerrar definitivamente suas atividades e descontinuar o funcionamento da Plataforma a qualquer tempo,
                mediante aviso prévio publicado em seus canais oficiais. Nesse caso, caberá exclusivamente ao Organizador realizar, dentro do
                prazo informado, o backup das informações que desejar preservar.
              </p>
            </VCardText>

            <VCardSubtitle class="text-h6 font-weight-medium mb-4">
              5.4 Reativação de conta
            </VCardSubtitle>
            <VCardText class="py-0">
              <p class="mb-4">
                Em casos de cancelamento voluntário ou suspensão por qualquer motivo, a reativação da conta dependerá de solicitação formal do
                Anunciante e aprovação da curadoria da Plataforma, estando sujeita à análise de conformidade e nova adesão contratual.
              </p>
            </VCardText>

            <VAlert
              type="error"
              variant="tonal"
              class="mb-6"
            >
              <VIcon
                icon="mdi-alert"
                class="me-2"
              />
              <strong>Limitação de Responsabilidade:</strong> A Plataforma não será responsável, em nenhuma hipótese, por perdas e danos indiretos,
              lucros cessantes, interrupção de negociações, perda de dados, contatos não realizados, ou qualquer consequência decorrente da
              suspensão ou rescisão de acesso.
            </VAlert>

            <VDivider class="my-8" />

            <!-- Seção 6: Avaliações -->
            <VCardSubtitle class="text-h5 font-weight-medium mb-6">
              6. AVALIAÇÃO DE ORGANIZADORES POR FORNECEDORES
            </VCardSubtitle>
            <VCardText class="py-0">
              <p class="mb-4">
                A Plataforma disponibiliza ferramenta para que os Organizadores de Evento possam avaliar os Fornecedores e Fornecedores possam
                avaliar os Organizadores com os quais tenham interagido ou contratado, com base em suas experiências reais de uso. As avaliações
                têm caráter público e visam promover a transparência, a confiança e a melhoria contínua dos serviços ofertados.
              </p>
            </VCardText>

            <VCardSubtitle class="text-h6 font-weight-medium mb-4">
              6.2 Critérios para avaliações
            </VCardSubtitle>
            <VList
              class="mb-6"
              rounded
            >
              <VListItem class="px-4 py-3">
                <VListItemContent>
                  <VIcon
                    icon="mdi-check-circle"
                    class="me-2 text-green"
                  />
                  Devem refletir exclusivamente experiências reais
                </VListItemContent>
              </VListItem>
              <VDivider />
              <VListItem class="px-4 py-3">
                <VListItemContent>
                  <VIcon
                    icon="mdi-account-heart"
                    class="me-2 text-blue"
                  />
                  Devem ser redigidas de forma ética, clara e respeitosa, evitando termos ofensivos, preconceituosos, discriminatórios ou caluniosos
                </VListItemContent>
              </VListItem>
              <VDivider />
              <VListItem class="px-4 py-3">
                <VListItemContent>
                  <VIcon
                    icon="mdi-shield-check"
                    class="me-2 text-orange"
                  />
                  Estão sujeitas à moderação pela Plataforma, que poderá excluir, a seu exclusivo critério, conteúdos que contrariem estes termos
                </VListItemContent>
              </VListItem>
              <VDivider />
              <VListItem class="px-4 py-3">
                <VListItemContent>
                  <VIcon
                    icon="mdi-gavel"
                    class="me-2 text-red"
                  />
                  Poderão ser utilizadas como critério de análise para aplicação de medidas administrativas ao Anunciante
                </VListItemContent>
              </VListItem>
            </VList>

            <VCardSubtitle class="text-h6 font-weight-medium mb-4">
              6.3 Responsabilidade pelo conteúdo das avaliações
            </VCardSubtitle>
            <VCardText class="py-0">
              <p class="mb-4">
                O conteúdo das avaliações é de responsabilidade exclusiva dos Fornecedores Anunciantes e Organizadores que as publicam, não
                representando a opinião institucional da Plataforma. A Empresa não responde, direta ou indiretamente, por eventual discordância,
                prejuízo de imagem ou impacto comercial decorrente de avaliações legítimas publicadas por terceiros.
              </p>
            </VCardText>

            <VDivider class="my-8" />

            <!-- Seção 7: Propriedade Intelectual -->
            <VCardSubtitle class="text-h5 font-weight-medium mb-6">
              7. PROPRIEDADE INTELECTUAL E LICENÇA DE USO
            </VCardSubtitle>

            <VCardSubtitle class="text-h6 font-weight-medium mb-4">
              7.1 Direitos da Plataforma
            </VCardSubtitle>
            <VCardText class="py-0">
              <p class="mb-4">
                Todos os elementos que compõem a Plataforma Eventos Connect — incluindo, mas não se limitando a: marca, nome empresarial, logotipo,
                layout, design gráfico, textos, vídeos, códigos, funcionalidades, bancos de dados e demais ativos intangíveis — são de titularidade
                exclusiva da MKT Connect ou de seus licenciadores, e encontram-se protegidos pelas legislações aplicáveis.
              </p>
            </VCardText>

            <VCardSubtitle class="text-h6 font-weight-medium mb-4">
              7.2 Proibições
            </VCardSubtitle>
            <VCardText class="py-0">
              <p class="mb-4">
                É expressamente proibido copiar, reproduzir, modificar, distribuir, comercializar, exibir, publicar ou, de qualquer forma, explorar
                economicamente, total ou parcialmente, qualquer conteúdo ou funcionalidade da Plataforma sem autorização prévia, expressa e por
                escrito da Empresa. A violação dessa regra ensejará responsabilidade civil e penal.
              </p>
            </VCardText>

            <VCardSubtitle class="text-h6 font-weight-medium mb-4">
              7.3 Licença de uso
            </VCardSubtitle>
            <VCardText class="py-0">
              <p class="mb-4">
                Ao aceitar estes Termos, o Usuário recebe da Empresa uma licença de uso limitada, não exclusiva, intransferível e revogável para
                acessar e utilizar a Plataforma exclusivamente para os fins e nos limites previstos neste instrumento.
              </p>
            </VCardText>

            <VDivider class="my-8" />

            <!-- Seção 8: Disposições Gerais -->
            <VCardSubtitle class="text-h5 font-weight-medium mb-6">
              8. DISPOSIÇÕES GERAIS
            </VCardSubtitle>

            <VCardSubtitle class="text-h6 font-weight-medium mb-4">
              8.1 Relação empresarial
            </VCardSubtitle>
            <VCardText class="py-0">
              <p class="mb-4">
                Considerando que este Termo regula relação entre pessoas jurídicas ou empreendedores(as) em contexto comercial, não se aplicam
                as disposições do Código de Defesa do Consumidor (CDC).
              </p>
            </VCardText>

            <VCardSubtitle class="text-h6 font-weight-medium mb-4">
              8.2 Atendimento e canais oficiais de suporte
            </VCardSubtitle>
            <VCardText class="py-0">
              <p class="mb-4">
                O suporte técnico e comercial será prestado exclusivamente por:
              </p>
            </VCardText>
            <VList
              class="mb-6"
              rounded
              lines="two"
            >
              <VListItem class="px-4 py-3">
                <VListItemContent>
                  <VIcon
                    icon="mdi-email"
                    class="me-2 text-primary"
                  />
                  E-mail: <a
                    href="mailto:comercial@eventosconnect.com.br"
                    class="text-decoration-none"
                  >comercial@eventosconnect.com.br</a>
                </VListItemContent>
              </VListItem>
              <VDivider />
              <VListItem class="px-4 py-3">
                <VListItemContent>
                  <VIcon
                    icon="mdi-chat"
                    class="me-2 text-primary"
                  />
                  Chat oficial na Plataforma (disponível após login)
                </VListItemContent>
              </VListItem>
              <VDivider />
              <VListItem class="px-4 py-3">
                <VListItemContent>
                  <VIcon
                    icon="mdi-whatsapp"
                    class="me-2 text-green"
                  />
                  Telefone/WhatsApp: <a
                    href="tel:+5531984879899"
                    class="text-decoration-none"
                  >(31) 98487-9899</a>
                </VListItemContent>
              </VListItem>
            </VList>

            <VCardText class="py-0">
              <p class="mb-4">
                O atendimento será realizado em dias úteis, de segunda a sexta-feira, das 8h às 17h (horário de Brasília). Mensagens enviadas a
                canais não oficiais ou fora do horário comercial poderão não ser consideradas para contagem de prazos contratuais.
              </p>
            </VCardText>

            <VCardSubtitle class="text-h6 font-weight-medium mb-4">
              8.3 Medidas de segurança e limitações técnicas
            </VCardSubtitle>
            <VCardText class="py-0">
              <p class="mb-4">
                A Empresa adota medidas técnicas e administrativas compatíveis com os padrões de segurança do mercado, incluindo conexão HTTPS,
                autenticação de usuários e armazenamento seguro em servidores de alto nível.
              </p>
              <p class="mb-4">
                O Usuário reconhece, no entanto, que nenhum sistema é imune a falhas, e compromete-se a adotar precauções básicas, como senhas fortes,
                atualização de dispositivos e não acesso por redes públicas. A Plataforma não garante disponibilidade ininterrupta nem ausência de
                falhas técnicas, isentando-se de responsabilidade por indisponibilidades temporárias, erros de sistema ou instabilidades decorrentes da internet.
              </p>
            </VCardText>

            <VAlert
              type="success"
              variant="tonal"
              class="mt-8"
            >
              <VIcon
                icon="mdi-check-circle"
                class="me-2"
              />
              <strong>Contato para Dúvidas:</strong> Se, após a leitura, restarem dúvidas quanto ao conteúdo deste Termo, você poderá entrar em
              contato com a Empresa pelo e-mail: <a
                href="mailto:contato@eventosconnect.com.br"
                class="text-decoration-none"
              >contato@eventosconnect.com.br</a>
            </VAlert>

            <p class="text-body-2 text-grey-darken-1 mt-8 text-center">
              <strong>Data da última atualização:</strong> 24 de junho de 2025
            </p>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>
  </VContainer>
</template>

<style scoped>
p {
  line-height: 1.75;
}

/* Estilo para links */
a {
  color: rgb(var(--v-theme-primary));
}

a:hover {
  text-decoration: underline !important;
}

/* Estilo para as seções */
.v-card-subtitle {
  color: rgb(var(--v-theme-on-surface)) !important;
  opacity: 1 !important;
}

/* Estilo para os alertas */
.v-alert {
  border-radius: 8px;
}

/* Responsividade para telas menores */
@media (max-width: 768px) {
  .v-card {
    margin-block: 0;
    margin-inline: 16px;
  }

  .v-card-title {
    font-size: 1.5rem !important;
    line-height: 1.3;
  }

  .v-card-subtitle {
    font-size: 1.1rem !important;
  }
}
</style>
